import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from 'react-leaflet';
import L, { LatLngBoundsExpression, Marker as LeafletMarker } from 'leaflet';
import { Link } from 'react-router-dom';
import { CARTO_ATTRIBUTION, CARTO_TILE_URL } from '../utils/basemap';

type Club = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  location?: string;
  latitude: number;
  longitude: number;
  hero_image?: string;
  logo?: string;
};

type Run = {
  id: string;
  club_id: string;
  day_of_week: string;
  start_time: string;
  location_address?: string | null;
  distance?: string | null;
  vibes: string[] | null;
  notes?: string | null;
  title?: string | null;
  is_recurring?: boolean;
  run_date?: string | null;
};

type ClubFollow = {
  club_id: string;
};

type ClubCardMeta = {
  followers: number;
  nextRunText: string;
  nextRunDay: string;
  nextRunTime: string;
  nextRunTitle: string | null;
  nextRunDistance: string | null;
  goingCount: number;
  interestedCount: number;
  nextRunTimestamp: number; // ms since epoch, Infinity if no upcoming runs
};

const DAY_OPTIONS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const VIBE_OPTIONS = [
  'Social',
  'Beginner-Friendly',
  'Fast',
  'Competitive',
  'Long Run',
  'Trail',
  'Workout',
  'Recovery',
  'Women-Led',
  'Dog-Friendly',
];

const TIME_OF_DAY_OPTIONS = ['Morning', 'Afternoon', 'Evening'];

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

function firstLineAddress(address?: string | null) {
  if (!address) return null;
  const parts = address.split(',').map((p) => p.trim());
  const street = parts[0];
  const city = parts[1] ?? null;
  const zipMatch = address.match(/\b\d{5}(-\d{4})?\b/);
  const zip = zipMatch ? zipMatch[0] : null;
  if (!city) return street;
  if (zip) return `${street}, ${city}, ${zip}`;
  return `${street}, ${city}`;
}

function parseTimeToMinutes(time: string): number {
  if (!time) return Number.MAX_SAFE_INTEGER;
  const trimmed = time.trim().toUpperCase();

  // "6:30 PM" / "12:00 AM" format
  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minutes = Number(ampmMatch[2]);
    const meridiem = ampmMatch[3];
    if (meridiem === 'AM' && hour === 12) hour = 0;
    if (meridiem === 'PM' && hour !== 12) hour += 12;
    return hour * 60 + minutes;
  }

  // "18:30" / "18:30:00" format (HTML time input / PostgreSQL time)
  const h24Match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (h24Match) {
    return Number(h24Match[1]) * 60 + Number(h24Match[2]);
  }

  return Number.MAX_SAFE_INTEGER;
}

function formatTime(time: string): string {
  if (!time) return '';
  const minutes = parseTimeToMinutes(time);
  if (minutes === Number.MAX_SAFE_INTEGER) return time;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayH = h % 12 === 0 ? 12 : h % 12;
  return `${displayH}:${String(m).padStart(2, '0')} ${suffix}`;
}

function getTimeOfDay(startTime: string): string | null {
  const totalMinutes = parseTimeToMinutes(startTime);

  if (totalMinutes === Number.MAX_SAFE_INTEGER) return null;
  if (totalMinutes < 12 * 60) return 'Morning';
  if (totalMinutes < 17 * 60) return 'Afternoon';
  return 'Evening';
}

function getNextRunDate(run: Run): Date | null {
  const dayIndex = WEEKDAY_TO_INDEX[run.day_of_week];
  const totalMinutes = parseTimeToMinutes(run.start_time);

  if (dayIndex === undefined || totalMinutes === Number.MAX_SAFE_INTEGER) {
    return null;
  }

  const now = new Date();
  const candidate = new Date(now);
  candidate.setHours(0, 0, 0, 0);

  const currentDay = now.getDay();
  let diff = dayIndex - currentDay;
  if (diff < 0) diff += 7;

  candidate.setDate(now.getDate() + diff);
  candidate.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);

  if (candidate <= now) {
    candidate.setDate(candidate.getDate() + 7);
  }

  return candidate;
}

function formatNextRunCountdown(nextRunDate: Date | null, startTime: string) {
  if (!nextRunDate) {
    return {
      nextRunText: 'No next run',
      nextRunSubtext: 'Schedule not available',
    };
  }

  const now = new Date();

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const nextDay = new Date(nextRunDate);
  nextDay.setHours(0, 0, 0, 0);

  const dayDiff = Math.round(
    (nextDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  let nextRunText = '';
  if (dayDiff === 0) {
    nextRunText = 'Today';
  } else if (dayDiff === 1) {
    nextRunText = 'Tomorrow';
  } else if (dayDiff < 7) {
    nextRunText = `In ${dayDiff} days`;
  } else {
    nextRunText = nextRunDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  return {
    nextRunText,
    nextRunSubtext: `${startTime}`,
  };
}

function getClubCardMeta(
  clubId: string,
  runs: Run[],
  followerCounts: Record<string, number>,
  rsvpCounts: Record<string, { going: number; interested: number }>,
  isoToday: string
): ClubCardMeta {
  const clubRuns = runs.filter((run) => run.club_id === clubId);
  const followers = followerCounts[clubId] || 0;

  const nextRunCandidates = clubRuns
    .map((run) => {
      let nextDate: Date | null = null;
      if (run.run_date) {
        // One-off run — use exact date; skip if already passed
        const d = new Date(run.run_date + 'T00:00:00');
        if (d >= new Date(isoToday + 'T00:00:00')) nextDate = d;
      } else {
        // Recurring run — calculate next occurrence from day_of_week
        nextDate = getNextRunDate(run);
      }
      return { run, nextDate };
    })
    .filter((item): item is { run: Run; nextDate: Date } => item.nextDate !== null)
    .sort((a, b) => a.nextDate.getTime() - b.nextDate.getTime());

  if (nextRunCandidates.length === 0) {
    return {
      followers,
      nextRunText: '—',
      nextRunDay: '',
      nextRunTime: '',
      nextRunTitle: null,
      nextRunDistance: null,
      goingCount: 0,
      interestedCount: 0,
      nextRunTimestamp: Infinity,
    };
  }

  const { run, nextDate } = nextRunCandidates[0];
  const { nextRunText } = formatNextRunCountdown(nextDate, run.start_time);
  const nextDateIso = nextDate.toISOString().slice(0, 10);
  const isToday = nextDateIso === isoToday;
  const counts = isToday ? (rsvpCounts[run.id] ?? { going: 0, interested: 0 }) : { going: 0, interested: 0 };

  return {
    followers,
    nextRunText,
    nextRunDay: run.day_of_week,
    nextRunTime: formatTime(run.start_time),
    nextRunTitle: run.title ?? null,
    nextRunDistance: run.distance ?? null,
    goingCount: counts.going,
    interestedCount: counts.interested,
    nextRunTimestamp: nextDate.getTime(),
  };
}

function GeolocationCenter() {
  const map = useMap();
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => map.setView([pos.coords.latitude, pos.coords.longitude], 13),
      () => {} // denied or error — San Diego default stays
    );
  }, [map]);
  return null;
}

function FitBounds({ clubs }: { clubs: Club[] }) {
  const map = useMap();

  useEffect(() => {
    if (clubs.length > 0) {
      const bounds: LatLngBoundsExpression = clubs.map((club) => [
        club.latitude,
        club.longitude,
      ]);
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, [clubs, map]);

  return null;
}

function FocusClubOnMap({
  selectedClub,
  markerRefs,
}: {
  selectedClub: Club | null;
  markerRefs: React.MutableRefObject<Record<string, LeafletMarker | null>>;
}) {
  const map = useMap();

  useEffect(() => {
    if (!selectedClub) return;

    map.flyTo([selectedClub.latitude, selectedClub.longitude], 13, {
      duration: 0.8,
    });

    const marker = markerRefs.current[selectedClub.id];
    if (marker) {
      setTimeout(() => {
        marker.openPopup();
      }, 250);
    }
  }, [selectedClub, map, markerRefs]);

  return null;
}

export default function ClubDirectory() {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [follows, setFollows] = useState<ClubFollow[]>([]);
  const [rsvpCounts, setRsvpCounts] = useState<Record<string, { going: number; interested: number }>>({});
  const [isoToday] = useState(() => {
    const now = new Date();
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
  });
  const [hoveredClubId, setHoveredClubId] = useState<string | null>(null);
  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedVibes, setSelectedVibes] = useState<string[]>([]);
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const markerRefs = useRef<Record<string, LeafletMarker | null>>({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = async () => {
    const now = new Date();
    const today = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');

    const [
      { data: clubsData, error: clubsError },
      { data: runsData, error: runsError },
      { data: followsData, error: followsError },
      { data: rsvpData },
    ] = await Promise.all([
      supabase.from('clubs').select('*'),
      supabase.from('runs').select('id, club_id, day_of_week, start_time, location_address, distance, vibes, notes, title, is_recurring, run_date').eq('is_active', true),
      supabase.from('club_follows').select('club_id'),
      supabase.from('run_rsvps').select('run_id, status').eq('run_date', today).in('status', ['going', 'interested']),
    ]);

    if (!clubsError && clubsData) {
      setClubs(clubsData);
    }

    if (!runsError && runsData) {
      const normalizedRuns = (runsData as Run[]).map((run) => ({
        ...run,
        vibes: Array.isArray(run.vibes) ? run.vibes : [],
      }));
      setRuns(normalizedRuns);
    }

    if (!followsError && followsData) {
      setFollows(followsData as ClubFollow[]);
    }

    if (rsvpData) {
      const counts: Record<string, { going: number; interested: number }> = {};
      for (const row of rsvpData) {
        if (!counts[row.run_id]) counts[row.run_id] = { going: 0, interested: 0 };
        if (row.status === 'going') counts[row.run_id].going++;
        if (row.status === 'interested') counts[row.run_id].interested++;
      }
      setRsvpCounts(counts);
    }
  };

  const followerCounts = useMemo(() => {
    return follows.reduce<Record<string, number>>((acc, follow) => {
      acc[follow.club_id] = (acc[follow.club_id] || 0) + 1;
      return acc;
    }, {});
  }, [follows]);

  const toggleVibe = (vibe: string) => {
    setSelectedVibes((prev) =>
      prev.includes(vibe)
        ? prev.filter((item) => item !== vibe)
        : [...prev, vibe]
    );
  };

  const clearFilters = () => {
    setSelectedDay(null);
    setSelectedVibes([]);
    setSelectedTimeOfDay(null);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const activeFilterCount = [
    selectedDay ? 1 : 0,
    selectedTimeOfDay ? 1 : 0,
    selectedVibes.length,
  ].reduce((sum, value) => sum + value, 0);

  const filteredClubIds = useMemo(() => {
    const hasDayFilter = !!selectedDay;
    const hasVibeFilter = selectedVibes.length > 0;
    const hasTimeOfDayFilter = !!selectedTimeOfDay;

    if (!hasDayFilter && !hasVibeFilter && !hasTimeOfDayFilter) {
      return new Set(clubs.map((club) => club.id));
    }

    const matchingRuns = runs.filter((run) => {
      const matchesDay = !selectedDay || run.day_of_week === selectedDay;

      const runVibes = Array.isArray(run.vibes) ? run.vibes : [];
      const matchesVibe =
        selectedVibes.length === 0 ||
        selectedVibes.some((vibe) => runVibes.includes(vibe));

      const runTimeOfDay = getTimeOfDay(run.start_time);
      const matchesTimeOfDay =
        !selectedTimeOfDay || runTimeOfDay === selectedTimeOfDay;

      return matchesDay && matchesVibe && matchesTimeOfDay;
    });

    return new Set(matchingRuns.map((run) => run.club_id));
  }, [clubs, runs, selectedDay, selectedVibes, selectedTimeOfDay]);

  const filteredClubs = useMemo(() => {
    return clubs.filter((club) => filteredClubIds.has(club.id));
  }, [clubs, filteredClubIds]);

  const searchFilteredClubs = useMemo(() => {
    if (!debouncedSearch) return filteredClubs;
    const q = debouncedSearch.toLowerCase();
    return filteredClubs.filter(
      (club) =>
        club.name.toLowerCase().includes(q) ||
        (club.description ?? '').toLowerCase().includes(q)
    );
  }, [filteredClubs, debouncedSearch]);

  const clubCardMeta = useMemo(() => {
    return searchFilteredClubs.reduce<Record<string, ClubCardMeta>>((acc, club) => {
      acc[club.id] = getClubCardMeta(club.id, runs, followerCounts, rsvpCounts, isoToday);
      return acc;
    }, {});
  }, [searchFilteredClubs, runs, followerCounts, rsvpCounts, isoToday]);

  const sortedFilteredClubs = useMemo(() => {
    return [...searchFilteredClubs].sort((a, b) => {
      const aTs = clubCardMeta[a.id]?.nextRunTimestamp ?? Infinity;
      const bTs = clubCardMeta[b.id]?.nextRunTimestamp ?? Infinity;
      return aTs - bTs;
    });
  }, [searchFilteredClubs, clubCardMeta]);

  useEffect(() => {
    if (!selectedClubId) return;

    const stillVisible = searchFilteredClubs.some((club) => club.id === selectedClubId);
    if (!stillVisible) {
      setSelectedClubId(null);
    }
  }, [selectedClubId, searchFilteredClubs]);

  const selectedClub =
    searchFilteredClubs.find((club) => club.id === selectedClubId) || null;

  const getIcon = (club: Club, isActive: boolean) => {
    const size = isActive ? 48 : 38;

    if (club.logo) {
      return L.divIcon({
        html: `
          <div style="
            width:${size}px;
            height:${size}px;
            border-radius:9999px;
            overflow:hidden;
            border:2px solid ${isActive ? '#000000' : '#ffffff'};
            box-shadow:${isActive ? '0 8px 22px rgba(0,0,0,0.35)' : '0 6px 14px rgba(0,0,0,0.25)'};
            background:white;
            transform:${isActive ? 'scale(1.08)' : 'scale(1)'};
          ">
            <img
              src="${club.logo}"
              alt="${club.name}"
              style="
                width:100%;
                height:100%;
                object-fit:cover;
                display:block;
              "
            />
          </div>
        `,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    }

    return L.divIcon({
      html: `
        <div style="
          background:${isActive ? '#ffde59' : '#000000'};
          color:${isActive ? '#000000' : '#ffffff'};
          width:${size}px;
          height:${size}px;
          display:flex;
          align-items:center;
          justify-content:center;
          border-radius:50%;
          font-size:${isActive ? '20px' : '18px'};
          border:${isActive ? '2px solid #000000' : 'none'};
          box-shadow:${isActive ? '0 8px 22px rgba(0,0,0,0.35)' : '0 6px 14px rgba(0,0,0,0.25)'};
          transform:${isActive ? 'scale(1.08)' : 'scale(1)'};
        ">
          🏃
        </div>
      `,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  const activeClubId = hoveredClubId || selectedClubId;

  return (
    <main className="min-h-screen overflow-x-hidden bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6">
      <div className="space-y-6">
        <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.08)]">
          <div className="bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-8 py-10 md:px-10 md:py-12">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/45">
              Clubs
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-black md:text-5xl">
              Run Clubs Near You
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-black/65 sm:text-base">
              Explore the map, then refine by day, vibe, and time of day.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
            <svg className="h-4 w-4 text-black/40" fill="none" stroke="currentColor" strokeWidth="2"
              viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clubs by name…"
            className="w-full rounded-2xl border border-black/10 bg-white py-3 pl-10 pr-4 text-sm text-black placeholder-black/35 shadow-sm outline-none transition focus:border-black/30 focus:ring-2 focus:ring-black/8"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute inset-y-0 right-3 flex items-center px-1 text-black/35 hover:text-black transition"
              aria-label="Clear search"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"
                viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-black/10 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="inline-flex items-center rounded-full bg-black px-4 py-2 text-sm font-semibold text-white transition hover:bg-black/90"
            >
              {filtersOpen ? 'Hide Filters' : 'Filters'}
            </button>

            <div className="rounded-full bg-[#f5f5f5] px-3 py-2 text-sm font-medium text-black">
              {searchFilteredClubs.length} {searchFilteredClubs.length === 1 ? 'club' : 'clubs'}
            </div>

            {activeFilterCount > 0 && (
              <div className="rounded-full bg-[#e2a9f1] px-3 py-2 text-sm font-semibold text-black">
                {activeFilterCount} active
              </div>
            )}

            {(selectedDay || selectedVibes.length > 0 || selectedTimeOfDay) && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-black hover:text-black"
              >
                Clear filters
              </button>
            )}
          </div>

          {filtersOpen && (
            <div className="mt-4 grid gap-4 border-t border-black/10 pt-4 lg:grid-cols-3">
              <div className="space-y-3">
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Day
                </h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedDay(null)}
                    className={`rounded-full px-4 py-2 text-sm font-medium border transition ${
                      selectedDay === null
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-black hover:text-black'
                    }`}
                  >
                    All Days
                  </button>

                  {DAY_OPTIONS.map((day) => {
                    const isActive = selectedDay === day;
                    return (
                      <button
                        key={day}
                        onClick={() => setSelectedDay(isActive ? null : day)}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition ${
                          isActive
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-black hover:text-black'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Time of Day
                </h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedTimeOfDay(null)}
                    className={`rounded-full px-4 py-2 text-sm font-medium border transition ${
                      selectedTimeOfDay === null
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-black hover:text-black'
                    }`}
                  >
                    All Times
                  </button>

                  {TIME_OF_DAY_OPTIONS.map((timeOfDay) => {
                    const isActive = selectedTimeOfDay === timeOfDay;
                    return (
                      <button
                        key={timeOfDay}
                        onClick={() => setSelectedTimeOfDay(isActive ? null : timeOfDay)}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition ${
                          isActive
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-black hover:text-black'
                        }`}
                      >
                        {timeOfDay}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Vibe
                </h2>
                <div className="flex flex-wrap gap-2">
                  {VIBE_OPTIONS.map((vibe) => {
                    const isActive = selectedVibes.includes(vibe);
                    return (
                      <button
                        key={vibe}
                        onClick={() => toggleVibe(vibe)}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition ${
                          isActive
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-black hover:text-black'
                        }`}
                      >
                        {vibe}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="relative">
          <div className="h-[480px] overflow-hidden rounded-[2rem] border border-gray-200 shadow-xl sm:h-[560px]">
            <MapContainer
              center={[32.72, -117.15]}
              zoom={11}
              className="h-full w-full"
            >
              <TileLayer attribution={CARTO_ATTRIBUTION} url={CARTO_TILE_URL} />

              {searchFilteredClubs.map((club) => {
                const isActive = activeClubId === club.id;

                return (
                  <Marker
                    key={club.id}
                    position={[club.latitude, club.longitude]}
                    icon={getIcon(club, isActive)}
                    ref={(ref) => {
                      markerRefs.current[club.id] = ref;
                    }}
                    eventHandlers={{
                      click: () => setSelectedClubId(club.id),
                      popupclose: () => {
                        if (selectedClubId === club.id) {
                          setSelectedClubId(null);
                        }
                      },
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <Link
                          to={`/clubs/${club.slug}`}
                          className="font-bold text-black hover:underline"
                        >
                          {club.name}
                        </Link>
                        {club.location && (
                          <p className="text-gray-500">{club.location}</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              <GeolocationCenter />
              <FitBounds clubs={searchFilteredClubs} />
              <FocusClubOnMap
                selectedClub={selectedClub}
                markerRefs={markerRefs}
              />
            </MapContainer>
          </div>
          <div className="pointer-events-none absolute inset-0 rounded-[2rem] bg-gradient-to-t from-black/10 via-transparent to-transparent" />
        </div>

        {searchFilteredClubs.length === 0 ? (
          <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] p-10 text-center shadow-sm">
            {debouncedSearch ? (
              <>
                <h3 className="text-xl font-semibold">No clubs found for "{debouncedSearch}"</h3>
                <p className="mt-2 text-black/60">
                  Try a different name or check your spelling.
                </p>
                <button
                  onClick={clearSearch}
                  className="mt-4 rounded-full bg-black px-5 py-2 text-sm font-semibold text-white transition hover:bg-black/80"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold">No clubs match those filters</h3>
                <p className="mt-2 text-black/60">
                  Try another day, time, or remove one of the vibe filters.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sortedFilteredClubs.map((club) => {
              const meta = clubCardMeta[club.id];
              const hasRsvps = (meta?.goingCount ?? 0) > 0 || (meta?.interestedCount ?? 0) > 0;

              return (
                <div
                  key={club.id}
                  onMouseEnter={() => setHoveredClubId(club.id)}
                  onMouseLeave={() => setHoveredClubId(null)}
                  onClick={() => setSelectedClubId(club.id)}
                  className="group overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm transition hover:border-black/20 hover:shadow-md"
                >
                  <Link to={`/clubs/${club.slug}`} className="flex min-w-0 items-center gap-3 p-4">
                    {/* Avatar / Logo */}
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-black/8 bg-black/5">
                      {club.logo ? (
                        <img
                          src={club.logo}
                          alt={`${club.name} logo`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-xl">🏃</span>
                      )}
                    </div>

                    {/* Main content */}
                    <div className="min-w-0 flex-1 overflow-hidden">
                      {/* Row 1: name + followers */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-bold leading-tight text-black">
                          {club.name}
                        </p>
                        {(meta?.followers ?? 0) >= 10 && (
                          <span className="flex-shrink-0 rounded-full bg-black/5 px-2 py-0.5 text-[11px] font-semibold text-black/50">
                            {meta!.followers} followers
                          </span>
                        )}
                      </div>

                      {/* Row 2: location */}
                      {club.location && (
                        <p className="mt-0.5 truncate text-xs text-black/40">
                          {firstLineAddress(club.location)}
                        </p>
                      )}

                      {/* Row 3: next run snapshot + RSVP counts */}
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {meta?.nextRunText && meta.nextRunText !== '—' ? (
                          <span className="block truncate text-xs font-medium text-black/55">
                            {meta.nextRunText}
                            {meta.nextRunDay ? ` · ${meta.nextRunDay}` : ''}
                            {meta.nextRunTime ? ` · ${meta.nextRunTime}` : ''}
                            {meta.nextRunTitle ? ` — ${meta.nextRunTitle}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-black/30">No upcoming runs</span>
                        )}

                        {hasRsvps && (
                          <span className="flex items-center gap-1.5">
                            {(meta?.goingCount ?? 0) > 0 && (
                              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                {meta.goingCount} going
                              </span>
                            )}
                            {(meta?.interestedCount ?? 0) > 0 && (
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                {meta.interestedCount} interested
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <section className="mt-10 border-t border-black bg-white">
        <div className="mx-auto max-w-7xl px-0 py-8 sm:px-0 md:py-12">
          <div className="rounded-[2rem] border border-black bg-black px-5 py-8 text-white shadow-[0_20px_60px_rgba(0,0,0,0.10)] sm:px-8 md:px-10">
            <p
              className="text-[11px] font-bold uppercase tracking-[0.18em]"
              style={{ color: '#e2a9f1' }}
            >
              Add your crew
            </p>
            <h2 className="mt-2 max-w-lg text-2xl font-black tracking-tight sm:text-3xl">
              Put your club on the map.
            </h2>
            <p className="mt-3 max-w-md text-sm leading-6 text-white/80">
              Help runners discover your group and weekly rhythm.
            </p>

            <div className="mt-5">
              <Link
                to="/create-club"
                className="inline-flex items-center justify-center rounded-2xl bg-[#ffde59] px-5 py-3 text-sm font-black text-black transition hover:scale-[1.02]"
              >
                Add Your Club
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </main>
  );
}